import React from 'react';
import { createPortal } from 'react-dom';

type Layout = { left: number; width: number; scrollWidth: number };

/**
 * Zweite waagerechte Scrollleiste, die am unteren Fensterrand klebt, solange das
 * Ende der Tabelle unterhalb des Viewports liegt. Die native Leiste sitzt am
 * Ende des Inhalts — bei langen Listen also erst nach viel Scrollen erreichbar.
 *
 * Absichtlich `position: fixed` in einem Portal statt `position: sticky`: mehrere
 * dieser Tabellen stecken in Karten mit `overflow-hidden`, und das wäre für ein
 * sticky Element schon der Scrollport, womit es nicht am Fenster kleben würde.
 */
export function DockedHorizontalScrollbar({ target }: { target: HTMLElement | null }) {
  const proxyRef = React.useRef<HTMLDivElement>(null);
  const [layout, setLayout] = React.useState<Layout | null>(null);

  React.useEffect(() => {
    if (!target) {
      setLayout(null);
      return;
    }

    let frame = 0;
    const measure = () => {
      frame = 0;
      const rect = target.getBoundingClientRect();
      const overflows = target.scrollWidth - target.clientWidth > 1;
      // Die native Leiste sitzt am unteren Rand des Containers. Ist die sichtbar
      // oder die Tabelle gar nicht im Bild, braucht es keine zweite Leiste.
      const nativeBarInView = rect.bottom <= window.innerHeight;
      if (!overflows || nativeBarInView || rect.top >= window.innerHeight || rect.width === 0) {
        setLayout(null);
        return;
      }
      const next: Layout = {
        left: rect.left,
        width: target.clientWidth,
        scrollWidth: target.scrollWidth,
      };
      setLayout((prev) =>
        prev && prev.left === next.left && prev.width === next.width && prev.scrollWidth === next.scrollWidth
          ? prev
          : next,
      );
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };

    measure();
    // Capture-Phase, weil das Scrollereignis eines inneren Containers nicht hochblubbert.
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    const observer = new ResizeObserver(schedule);
    observer.observe(target);
    // Zeilen kommen und gehen, ohne den Container selbst zu verändern.
    if (target.firstElementChild) observer.observe(target.firstElementChild);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      observer.disconnect();
    };
  }, [target]);

  const hasLayout = layout !== null;
  React.useEffect(() => {
    const proxy = proxyRef.current;
    if (!target || !proxy) return;
    // Gleichsetzen löst kein weiteres Scrollereignis aus, darum endet das Spiegeln von selbst.
    const mirror = (from: HTMLElement, to: HTMLElement) => {
      if (to.scrollLeft !== from.scrollLeft) to.scrollLeft = from.scrollLeft;
    };
    const onTargetScroll = () => mirror(target, proxy);
    const onProxyScroll = () => mirror(proxy, target);
    proxy.scrollLeft = target.scrollLeft;
    target.addEventListener('scroll', onTargetScroll, { passive: true });
    proxy.addEventListener('scroll', onProxyScroll, { passive: true });
    return () => {
      target.removeEventListener('scroll', onTargetScroll);
      proxy.removeEventListener('scroll', onProxyScroll);
    };
  }, [target, hasLayout]);

  if (!layout) return null;

  return createPortal(
    <div
      className="fixed bottom-0 z-30 hidden border-t border-app-border bg-app-surface/95 backdrop-blur-sm md:block"
      style={{ left: layout.left, width: layout.width }}
    >
      <div ref={proxyRef} className="app-scrollbar overflow-x-auto overflow-y-hidden">
        <div style={{ width: layout.scrollWidth, height: 1 }} aria-hidden />
      </div>
    </div>,
    document.body,
  );
}
